import React, { Component, Fragment } from "react";
import axios from "axios";
import FeatherIcon from "feather-icons-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import ReactToPrint from "react-to-print";
import * as XLSX from "xlsx";

const studentName = (student) => {
    const user = student?.user || {};
    return [user.last_name, user.family_name, user.first_name]
        .filter(Boolean).join(" ").trim();
};

const roomLabel = (room) => {
    if (!room) return "Classe non renseignée";
    return [room.class?.short_entitled_class, room.title,
        room.section?.short_entitled_section].filter(Boolean).join(" ").trim();
};

const sortRooms = (a, b) => roomLabel(a).localeCompare(roomLabel(b), "fr", {
    numeric: true, sensitivity: "base",
});

const sortStudents = (a, b) =>
    roomLabel(a.sectionclass).localeCompare(roomLabel(b.sectionclass), "fr", {
        numeric: true, sensitivity: "base",
    }) ||
    studentName(a).localeCompare(studentName(b), "fr", {
        sensitivity: "base",
    }) ||
    String(a.matricule || "").localeCompare(String(b.matricule || ""), "fr", {
        numeric: true, sensitivity: "base",
    });

class Affectation extends Component {
    state = { students: [], rooms: [], selectedRoomIds: [], loading: true };

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        this.setState({ loading: true });
        try {
            const [studentRequest, roomRequest] = await Promise.all([
                axios.get("students"), axios.get("sectionclasses"),
            ]);
            const students = Array.isArray(studentRequest.data)
                ? studentRequest.data.filter((student) => Number(student.status) === 1) : [];
            const rooms = Array.isArray(roomRequest.data)
                ? roomRequest.data.filter((room) => room?.id && room.operation !== "Supprimer") : [];
            const roomsById = new Map(rooms.map((room) => [room.id, room]));

            students.forEach((student) => {
                if (student.sectionclass?.id && !roomsById.has(student.sectionclass.id)) {
                    roomsById.set(student.sectionclass.id, student.sectionclass);
                }
            });
            this.setState({
                students,
                rooms: Array.from(roomsById.values()).sort(sortRooms),
                loading: false,
            });
        } catch (error) {
            this.setState({ loading: false });
            toast(error.response?.data?.msg || "Impossible de charger les données d'affectation.", {
                theme: "light", style: { backgroundColor: "red", color: "white" },
            });
        }
    };

    toggleRoom = (roomId) => this.setState((state) => ({
        selectedRoomIds: state.selectedRoomIds.includes(roomId)
            ? state.selectedRoomIds.filter((id) => id !== roomId)
            : [...state.selectedRoomIds, roomId],
    }));

    getAssignments = () => {
        const selectedIds = new Set(this.state.selectedRoomIds);
        const rooms = this.state.rooms
            .filter((room) => selectedIds.has(room.id)).sort(sortRooms);
        if (!rooms.length) return [];

        const students = this.state.students
            .filter((student) => selectedIds.has(student.sectionclass?.id))
            .sort(sortStudents);
        const assignments = rooms.map((room) => ({ room, students: [] }));

        // Conserver les eleves de chaque classe d'origine par groupes d'au moins 2.
        // Chaque groupe reste alphabetique et les salles les moins remplies sont
        // choisies en priorite afin de maintenir un bon equilibre general.
        const studentsByOriginRoom = new Map();
        students.forEach((student) => {
            const originRoomId = student.sectionclass?.id || 'sans-classe';
            if (!studentsByOriginRoom.has(originRoomId)) {
                studentsByOriginRoom.set(originRoomId, []);
            }
            studentsByOriginRoom.get(originRoomId).push(student);
        });

        studentsByOriginRoom.forEach((originStudents) => {
            const roomCount = originStudents.length < 2
                ? 1
                : Math.min(assignments.length, Math.floor(originStudents.length / 2));
            const selectedAssignments = [...assignments]
                .sort((a, b) => a.students.length - b.students.length ||
                    sortRooms(a.room, b.room))
                .slice(0, roomCount)
                .sort((a, b) => sortRooms(a.room, b.room));
            const minimumGroupSize = originStudents.length < 2 ? 1 : 2;
            const groupSizes = selectedAssignments.map(() => minimumGroupSize);
            let remaining = originStudents.length - (minimumGroupSize * roomCount);

            // Ajouter les eleves restants aux groupes dont la salle sera la moins chargee.
            while (remaining > 0) {
                let lightestIndex = 0;
                selectedAssignments.forEach((assignment, index) => {
                    const projectedSize = assignment.students.length + groupSizes[index];
                    const lightestSize = selectedAssignments[lightestIndex].students.length +
                        groupSizes[lightestIndex];
                    if (projectedSize < lightestSize) lightestIndex = index;
                });
                groupSizes[lightestIndex] += 1;
                remaining -= 1;
            }

            let offset = 0;
            selectedAssignments.forEach((assignment, index) => {
                assignment.students.push(...originStudents.slice(offset, offset + groupSizes[index]));
                offset += groupSizes[index];
            });
        });
        return assignments;
    };

    exportExcel = (assignments) => {
        const rows = assignments.flatMap(({ room, students }) =>
            students.map((student, index) => ({
                "N° dans la salle": index + 1,
                Matricule: student.matricule || "",
                "Nom et Post-Nom": studentName(student),
                "Classe d'origine": roomLabel(student.sectionclass),
                Affectation: roomLabel(room),
                Observation: "",
            }))
        );
        if (!rows.length) {
            toast("Sélectionnez au moins une salle contenant des élèves.", { theme: "light" });
            return;
        }
        const sheet = XLSX.utils.json_to_sheet(rows);
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, "Affectations");
        XLSX.writeFile(book,
            `evivesoma_affectation_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    renderRows = (assignments) => {
        if (this.state.loading) return (
            <tbody><tr><td colSpan={5} className="affectation-table-state"><FeatherIcon className="user-search-spin" icon="loader" /></td></tr></tbody>
        );
        if (!assignments.length) return (
            <tbody><tr><td colSpan={5}>
                <div className="affectation-empty">
                    <span className="affectation-empty__icon"><FeatherIcon icon="users" size="24" /></span>
                    <strong>Aucune répartition à afficher</strong>
                    <small>Sélectionnez les classes à inclure depuis le panneau de droite.</small>
                </div>
            </td></tr></tbody>
        );
        return (
            <tbody>
                {assignments.map(({ room, students }) => (
                    <Fragment key={room.id}>
                        <tr className="affectation-room-row">
                            <td colSpan={5}>
                                <span className="affectation-room-row__icon"><FeatherIcon icon="map-pin" size="14" /></span>
                                <strong>{roomLabel(room)}</strong>
                                <span className="affectation-room-row__count">
                                    {students.length} élève{students.length > 1 ? "s" : ""}
                                </span>
                            </td>
                        </tr>
                        {!students.length ? (
                            <tr><td colSpan={5} className="affectation-table-state">
                                Aucun élève à affecter dans cette classe
                            </td></tr>
                        ) : students.map((student, index) => (
                            <tr key={`${room.id}-${student.id}`}>
                                <td className="affectation-table__number">{index + 1}</td>
                                <td className="affectation-table__student">{studentName(student)}</td>
                                <td><span className="affectation-matricule">{student.matricule || "—"}</span></td>
                                <td>{roomLabel(student.sectionclass)}</td>
                                <td className="affectation-table__observation"></td>
                            </tr>
                        ))}
                    </Fragment>
                ))}
            </tbody>
        );
    };

    render() {
        const assignments = this.getAssignments();
        const total = assignments.reduce((sum, item) => sum + item.students.length, 0);
        const counts = assignments.map((item) => item.students.length);
        const difference = counts.length ? Math.max(...counts) - Math.min(...counts) : 0;

        return (
            <div className="affectation-page">
                <Helmet><title>{process.env.REACT_APP_NAME} | Répartition des élèves</title></Helmet>

                <header className="affectation-hero">
                    <div className="affectation-hero__content">
                        <span className="affectation-hero__icon"><FeatherIcon icon="shuffle" size="24" /></span>
                        <div>
                            <span className="affectation-eyebrow">Organisation des évaluations</span>
                            <h1>Répartition des élèves en salles</h1>
                            <p>Composez des groupes équilibrés pour les examens, évaluations et autres activités scolaires.</p>
                        </div>
                    </div>
                    <div className="affectation-hero__tools">
                        <Link to="/manuel#affectations" className="affectation-hero__doc"
                            title="Consulter le manuel de la répartition des élèves">
                            <FeatherIcon icon="book-open" size="15" /> Doc
                        </Link>
                        <div className="affectation-hero__status">
                            <span className={assignments.length ? "is-ready" : ""}></span>
                            {assignments.length ? "Répartition prête" : "En attente de sélection"}
                        </div>
                    </div>
                </header>

                <div className="affectation-stats" aria-label="Résumé de la répartition">
                    <div className="affectation-stat">
                        <span className="affectation-stat__icon"><FeatherIcon icon="users" size="18" /></span>
                        <div><strong>{total}</strong><span>Élèves répartis</span></div>
                    </div>
                    <div className="affectation-stat">
                        <span className="affectation-stat__icon"><FeatherIcon icon="grid" size="18" /></span>
                        <div><strong>{assignments.length}</strong><span>Salles retenues</span></div>
                    </div>
                    <div className="affectation-stat">
                        <span className="affectation-stat__icon"><FeatherIcon icon="activity" size="18" /></span>
                        <div><strong>{difference}</strong><span>Écart maximal</span></div>
                    </div>
                </div>

                <div className="affectation-layout">
                    <main className="affectation-panel affectation-preview">
                        <div className="affectation-panel__header">
                            <div>
                                <span className="affectation-section-label">Aperçu du document</span>
                                <h2>Liste de répartition</h2>
                                <p>Document prêt à être imprimé ou exporté.</p>
                            </div>
                            <div className="affectation-actions">
                                <ReactToPrint
                                    trigger={() => <button className="ev-button ev-button--secondary"
                                        type="button" disabled={!assignments.length}>
                                        <FeatherIcon icon="printer" size="16" /> Imprimer
                                    </button>}
                                    content={() => this.componentRef}
                                />
                                <button type="button" className="ev-button ev-button--primary"
                                    onClick={() => this.exportExcel(assignments)} disabled={!assignments.length}>
                                    <FeatherIcon icon="download" size="16" /> Exporter Excel
                                </button>
                            </div>
                        </div>
                        <div className="affectation-panel__body">
                            <div className="affectation-sheet" ref={(element) => (this.componentRef = element)}>
                                <div className="affectation-sheet__heading">
                                    <span>Document d’organisation</span>
                                    <h3>Liste de répartition des élèves</h3>
                                    <p>{total} élève{total > 1 ? "s" : ""} • {assignments.length} salle{assignments.length > 1 ? "s" : ""}</p>
                                </div>
                                <div className="affectation-table-wrap">
                                    <table className="affectation-table">
                                        <thead><tr>
                                            <th>N°</th><th>Nom et post-nom</th><th>Matricule</th>
                                            <th>Classe d’origine</th><th>Observation</th>
                                        </tr></thead>
                                        {this.renderRows(assignments)}
                                    </table>
                                </div>
                            </div>
                        </div>
                    </main>

                    <aside className="affectation-panel affectation-selector">
                        <div className="affectation-panel__header">
                            <div>
                                <span className="affectation-section-label">Étape 1</span>
                                <h2>Classes à répartir</h2>
                                <p>Sélectionnez les classes concernées par l’activité.</p>
                            </div>
                            <span className="affectation-selection-count">
                                {this.state.selectedRoomIds.length}/{this.state.rooms.length}
                            </span>
                        </div>
                        <div className="affectation-selector__tools">
                            <button type="button" className="affectation-text-button"
                                onClick={() => this.setState({ selectedRoomIds: this.state.rooms.map((room) => room.id) })}
                                disabled={!this.state.rooms.length}>
                                <FeatherIcon icon="check-square" size="14" /> Tout sélectionner
                            </button>
                            <button type="button" className="affectation-text-button"
                                onClick={() => this.setState({ selectedRoomIds: [] })}
                                disabled={!this.state.selectedRoomIds.length}>
                                <FeatherIcon icon="x" size="14" /> Effacer
                            </button>
                        </div>
                        <div className="affectation-room-list">
                            {this.state.rooms.map((room) => {
                                const isSelected = this.state.selectedRoomIds.includes(room.id);
                                const roomStudentCount = this.state.students.filter(
                                    (student) => student.sectionclass?.id === room.id
                                ).length;
                                return (
                                    <label htmlFor={`room-${room.id}`}
                                        className={`affectation-room-option${isSelected ? " is-selected" : ""}`}
                                        key={room.id}>
                                        <input id={`room-${room.id}`} type="checkbox" checked={isSelected}
                                            onChange={() => this.toggleRoom(room.id)} />
                                        <span className="affectation-room-option__check">
                                            <FeatherIcon icon="check" size="14" />
                                        </span>
                                        <span className="affectation-room-option__content">
                                            <strong>{roomLabel(room)}</strong>
                                            <small>{roomStudentCount} élève{roomStudentCount > 1 ? "s" : ""}</small>
                                        </span>
                                        <FeatherIcon className="affectation-room-option__arrow" icon="chevron-right" size="16" />
                                    </label>
                                );
                            })}
                            {!this.state.loading && !this.state.rooms.length &&
                                <div className="affectation-empty">
                                    <FeatherIcon icon="inbox" size="24" />
                                    <strong>Aucune classe disponible</strong>
                                </div>}
                        </div>
                        <div className="affectation-selector__note">
                            <FeatherIcon icon="info" size="15" />
                            <span>La répartition est recalculée automatiquement après chaque sélection.</span>
                        </div>
                    </aside>
                </div>
            </div>
        );
    }
}

export default Affectation;
